import { useRef } from "react";
import { FadeIn } from "@/components/FadeIn";
import { ArrowLeft, ArrowRight, Home, Wheat, Leaf, GraduationCap, Compass } from "lucide-react";

const NOTES = [
  {
    id: "landowners",
    icon: Home,
    title: "The landowner",
    body: "You've looked at that pond a hundred times and wondered what it could become. Before you find out the expensive way, find out the informed way.",
  },
  {
    id: "farms",
    icon: Wheat,
    title: "The farm",
    body: (
      <>
        Aquaculture can complement a working farm — or quietly drain it. We help you see the real numbers, seasons, and labour before
        <br className="hidden lg:block" /> you commit.
      </>
    ),
  },
  {
    id: "stewards",
    icon: Leaf,
    title: "The steward",
    body: "You care about responsible pond management, native species, water quality, and the long-term health of your land. We share that commitment.",
  },
  {
    id: "educators",
    icon: GraduationCap,
    title: "The educator",
    body: "Schools, colleges, and community groups come to us for credible, Alberta-specific teaching on pond ecosystems and aquaculture.",
  },
  {
    id: "newcomers",
    icon: Compass,
    title: "The newcomer",
    body: "You're at the very beginning, and we help you understand your options before choosing suppliers, contractors, or next steps.",
  },
];

export const WhoWeHelp = () => {
  const stripRef = useRef(null);

  const scrollStrip = (dir) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 380, behavior: "smooth" });
  };

  return (
    <section id="who-we-help" data-testid="who-we-help-section" className="bg-[color:var(--apn-sage-2)]/50">
      <div className="mx-auto w-full max-w-[1280px] px-[15px] sm:px-8 pt-[50px] lg:pt-20 pb-[33px] sm:pb-16">
        <FadeIn>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-serif-heading font-medium text-4xl sm:text-5xl lg:text-[clamp(2.25rem,2.9vw+0.5rem,3.4rem)] leading-[1.05] tracking-[-0.015em] text-foreground max-w-[18ch] lg:max-w-none">
              Written for people who
              <br className="hidden lg:block" /> take their land seriously
            </h2>
            <div className="flex items-center gap-3 pb-2">
              <button
                type="button"
                onClick={() => scrollStrip(-1)}
                data-testid="who-we-help-prev-button"
                aria-label="Scroll cards left"
                className="inline-flex h-11 w-11 items-center justify-center border border-[color:var(--apn-border)] bg-[color:var(--apn-paper-2)] text-foreground transition-colors duration-200 hover:bg-secondary"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => scrollStrip(1)}
                data-testid="who-we-help-next-button"
                aria-label="Scroll cards right"
                className="inline-flex h-11 w-11 items-center justify-center border border-[color:var(--apn-border)] bg-[color:var(--apn-paper-2)] text-foreground transition-colors duration-200 hover:bg-secondary"
              >
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
              </button>
            </div>
          </div>
        </FadeIn>
      </div>

      {/* Horizontal field-notes strip, bleeding to the right edge */}
      <div className="pb-[50px] lg:pb-10">
        <FadeIn>
          <div className="pl-[15px] sm:pl-8 lg:pl-[max(2rem,calc((100vw-1280px)/2+2rem))]">
            <div ref={stripRef} className="scroll-strip no-scrollbar pr-[15px] sm:pr-8" data-testid="who-we-help-strip">
              {NOTES.map((note) => {
                const Icon = note.icon;
                return (
                  <article
                    key={note.id}
                    data-testid={`who-we-help-note-${note.id}`}
                    className={`note-slip shrink-0 w-[78vw] sm:w-[340px] p-7 sm:p-9 lg:px-[26px] ${
                      note.id === "landowners" ? "max-lg:px-[21px]" : note.id === "farms" ? "max-lg:px-[23px]" : ""
                    }`}
                  >
                    <Icon
                      className="h-6 w-6 text-[color:var(--apn-forest)]"
                      strokeWidth={1.25}
                      aria-hidden="true"
                    />
                    <h3 className="mt-5 font-serif-heading text-xl sm:text-2xl font-medium leading-snug text-foreground whitespace-nowrap">
                      {note.title}
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-foreground/65">{note.body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};
